import { create } from 'zustand';
import type { ScanResult } from '@/lib/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScanStats {
  total: number;
  valid: number;
  invalid: number;
  duplicate: number;
}

interface ScanState {
  todayStats: ScanStats;
  lastScanResult: ScanResult | null;

  recordValid: () => void;
  recordInvalid: () => void;
  recordDuplicate: () => void;
  setLastResult: (result: ScanResult) => void;
  clearLastResult: () => void;
  resetDaily: () => void;
  loadTodayStats: () => void;
}

// ---------------------------------------------------------------------------
// localStorage helpers — stats keyed by date (YYYY-MM-DD)
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'validation-stats';

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function readAllStats(): Record<string, ScanStats> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, ScanStats>) : {};
  } catch {
    return {};
  }
}

function readTodayStats(): ScanStats {
  const all = readAllStats();
  return all[todayKey()] ?? { total: 0, valid: 0, invalid: 0, duplicate: 0 };
}

function writeTodayStats(stats: ScanStats): void {
  try {
    const all = readAllStats();
    all[todayKey()] = stats;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Storage quota exceeded — ignore.
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_STATS: ScanStats = { total: 0, valid: 0, invalid: 0, duplicate: 0 };

function increment(stats: ScanStats, field: 'valid' | 'invalid' | 'duplicate'): ScanStats {
  const next: ScanStats = {
    ...stats,
    total: stats.total + 1,
    [field]: stats[field] + 1,
  };
  writeTodayStats(next);
  return next;
}

// ---------------------------------------------------------------------------
// Store (runtime — stats mirrored to localStorage)
// ---------------------------------------------------------------------------

export const useScanStore = create<ScanState>()((set) => ({
  todayStats: readTodayStats(),
  lastScanResult: null,

  recordValid: () =>
    set((s) => ({ todayStats: increment(s.todayStats, 'valid') })),

  recordInvalid: () =>
    set((s) => ({ todayStats: increment(s.todayStats, 'invalid') })),

  recordDuplicate: () =>
    set((s) => ({ todayStats: increment(s.todayStats, 'duplicate') })),

  setLastResult: (result) => set({ lastScanResult: result }),

  clearLastResult: () => set({ lastScanResult: null }),

  resetDaily: () => {
    const fresh = { ...EMPTY_STATS };
    writeTodayStats(fresh);
    set({ todayStats: fresh, lastScanResult: null });
  },

  loadTodayStats: () => {
    set({ todayStats: readTodayStats() });
  },
}));
